import { useEffect, useState } from 'react'
import { X, Clock3, Loader2, AlertCircle } from 'lucide-react'
import { Card } from '../../../../../../components/ui/Card'

function formatDateTimeLocal(dateStr) {
  if (dateStr == null || dateStr === '') return ''
  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
    if (!date || isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  } catch {
    return ''
  }
}

export function EditPeriodEndModal({
  period,
  onClose,
  onSubmit,
  onError,
  isSubmitting,
}) {
  const [periodEnd, setPeriodEnd] = useState('')
  const [validationError, setValidationError] = useState(null)

  useEffect(() => {
    if (!period) return
    const endRaw = period.PeriodEnd || period.periodEnd || period.period_end || ''
    setPeriodEnd(formatDateTimeLocal(endRaw))
    setValidationError(null)
  }, [period])

  if (!period) return null

  const handleSubmit = async (e) => {
    e?.preventDefault()
    e?.stopPropagation()

    const endDate = periodEnd ? new Date(periodEnd) : null

    if (endDate && isNaN(endDate.getTime())) {
      setValidationError('La fecha de fin del período es inválida')
      return
    }

    // Validación: verificar que end > start
    const startRaw = period.PeriodStart || period.periodStart || period.period_start
    if (endDate && startRaw) {
      const startDate = new Date(startRaw)
      if (!isNaN(startDate.getTime()) && endDate <= startDate) {
        const msg = 'No se puede ajustar la fecha de fin a una fecha anterior o igual a la de inicio del período'
        setValidationError(msg)
        if (onError) onError(msg)
        return
      }
    }

    setValidationError(null)
    await onSubmit?.({
      PeriodEnd: periodEnd,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Clock3 className="w-5 h-5" />
              Editar Fin del Período
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Cerrar modal"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="space-y-4">
            {validationError && (
              <div className="bg-red-900/30 border border-red-500 rounded-lg p-3 flex items-start gap-2" role="alert">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-red-400 text-sm">{validationError}</span>
              </div>
            )}

            <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
              <p className="text-xs text-gray-400 mb-1">Período</p>
              <p className="text-white font-mono text-sm">#{period.ID || period.id || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Fecha y hora fin del período
              </label>
              <input
                type="datetime-local"
                value={periodEnd}
                onChange={(e) => {
                  setPeriodEnd(e.target.value)
                  setValidationError(null)
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                disabled={isSubmitting}
              />
            </div>

            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3">
              <p className="text-xs text-blue-300">
                <strong>Nota:</strong> Si este es el último período, se actualizará también la fecha de fin de la orden. 
                El inicio del período siguiente se ajustará automáticamente.
              </p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-white">Guardando...</span>
                  </>
                ) : (
                  <span className="text-white">Guardar cambios</span>
                )}
              </button>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed rounded-lg transition-colors text-white"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
